import { useMemo, useState } from 'react';
import {
  EMPTY_FILTERS,
  type ClaimFilters,
  type ClaimRow,
  type SortState,
  type SortableColumn,
} from '../types/claims.types';
import { hasActiveFilters, matchesView, sortClaims, visibleLines } from '../utils/claim-filters';

export const PAGE_SIZE = 10;

interface ClaimsView {
  readonly rows: readonly ClaimRow[];
  readonly totalMatches: number;
  readonly page: number;
  readonly pageCount: number;
  readonly keyword: string;
  readonly sort: SortState | null;
  readonly filters: ClaimFilters;
  /** Whether anything has been changed from the default view, and so whether Reset has work to do. */
  readonly viewChanged: boolean;
  /** The lines of a claim that belong in the grid under the current search and filters. */
  linesOf: (claim: ClaimRow) => readonly ClaimRow[];
  setKeyword: (value: string) => void;
  toggleSort: (column: SortableColumn) => void;
  applyFilters: (filters: ClaimFilters) => void;
  resetView: () => void;
  goToPage: (page: number) => void;
}

/**
 * Search, sort, filter and pagination over the claims already loaded for the active policy
 * (US-010, US-015). All of it is client-side over the scoped result, so no view change can widen the
 * data beyond the searched policy (BR-03).
 */
/**
 * What Reset has to undo, and therefore what enables the button. Keyword and sort count: with only the
 * filters consulted, sorting a column or typing a search left Reset disabled, and clicking it after
 * applying a filter cleared the filter while quietly leaving the sort and the search in place (PP-023).
 */
function isChangedFromDefault(
  keyword: string,
  sort: SortState | null,
  filters: ClaimFilters,
): boolean {
  return keyword.trim().length > 0 || sort !== null || hasActiveFilters(filters);
}

export function useClaimsView(claims: readonly ClaimRow[], resetKey: string): ClaimsView {
  const [keyword, setKeywordState] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<ClaimFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [appliedTo, setAppliedTo] = useState(resetKey);

  // A new policy starts from a clean view. Carrying a previous policy's filters over would show the
  // provider a silently narrowed subset of a different policy's claims (US-013 AC-002).
  if (appliedTo !== resetKey) {
    setAppliedTo(resetKey);
    setKeywordState('');
    setSort(null);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }

  const matches = useMemo(
    () =>
      sortClaims(
        claims.filter((claim) => matchesView(claim, keyword, filters)),
        sort,
      ),
    [claims, keyword, filters, sort],
  );

  const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const rows = matches.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return {
    rows,
    totalMatches: matches.length,
    page: currentPage,
    pageCount,
    keyword,
    sort,
    filters,
    viewChanged: isChangedFromDefault(keyword, sort, filters),
    linesOf: (claim): readonly ClaimRow[] => visibleLines(claim, keyword, filters),
    setKeyword: (value): void => {
      setKeywordState(value);
      setPage(1);
    },
    toggleSort: (column): void => {
      setSort((current) =>
        current?.column === column && current.direction === 'asc'
          ? { column, direction: 'desc' }
          : { column, direction: 'asc' },
      );
    },
    applyFilters: (next): void => {
      setFilters(next);
      setPage(1);
    },
    /** Back to the default view (PP-023): no search, no sort, no filters, first page. */
    resetView: (): void => {
      setKeywordState('');
      setSort(null);
      setFilters(EMPTY_FILTERS);
      setPage(1);
    },
    goToPage: setPage,
  };
}
